// Spor A (XDIS-EPJ 4.1–4.13, 5.6): state and checks of the XDIS21 plejeforløbsplan.
// Patient identifier and EpisodeOfCareIdentifier are not part of the form: Communicate
// takes them from the received XDIS16 (sourceExchangeId). Limits follow the XSD.

import type { DateTime, Party } from './xdis18Form';
import {
  signedByFromPrefill,
  type SignedBy,
} from './xdisSignedBy';

export type { DateTime, Party, SignedBy };
export { signedByFromPractitioner, withSignedByFallback } from './xdisSignedBy';

export const XDIS21_TESTCENTER_EAN = '5790001382445';

export const NURSING_AREA_CODES = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K'] as const;
export type NursingAreaCode = (typeof NURSING_AREA_CODES)[number];

export const NURSING_AREA_I18N: Record<NursingAreaCode, string> = {
  A: 'nursing_a',
  B: 'nursing_b',
  C: 'nursing_c',
  D: 'nursing_d',
  E: 'nursing_e',
  F: 'nursing_f',
  G: 'nursing_g',
  H: 'nursing_h',
  I: 'nursing_i',
  J: 'nursing_j',
  K: 'nursing_k',
};

/** Selectable scores from Communicate PR #55. uden_specifikation is never offered. */
export const ABILITY_SCORES = [
  'ubetydelige',
  'lette',
  'moderate',
  'svaere',
  'totale',
  'ikke_relevant',
] as const;
export type AbilityScore = (typeof ABILITY_SCORES)[number];

export function selectableAbilityScores(scores: readonly string[]): string[] {
  return scores.filter((score) => Boolean(score) && score !== 'uden_specifikation' && score !== 'ikke_vurderet');
}

export const COORDINATION_SUBJECTS = [
  'homeChanges',
  'scheduleAuxilaries',
  'rehabilitationNeeds',
  'homeCareNeeds',
  'medicineAdministrationHelp',
  'medicineConsumptionHelp',
  'otherMunicipalWorkNeeded',
  'psychiatryAdmissionPlanNeeded',
] as const;
export type CoordinationSubject = (typeof COORDINATION_SUBJECTS)[number];

export const DISCHARGE_DATE_CODES = ['forslag', 'aftalt'] as const;
export type DischargeDateCode = (typeof DISCHARGE_DATE_CODES)[number];

/** Communicate `psychiatricDepartmentCodes` (build.ts ~119–122). Field 4.1.16 only for these. */
export const PSYCHIATRY_SPECIALITIES = ['psykiatri_sygehus', 'boerne_ungdomspsykiatri'] as const;

export function isPsychiatrySpeciality(code: string | null | undefined, codes: readonly string[] = PSYCHIATRY_SPECIALITIES): boolean {
  return Boolean(code && codes.includes(code));
}

export type CoordinationNeed = { needed: boolean; comment: string };

export type AbilityElement = {
  code: string;
  codeSystem: string;
  codeDescription: string;
  score: string;
  comment: string;
};

export const DEFAULT_ABILITY_ELEMENTS: AbilityElement[] = [
  { code: 'FA510', codeSystem: 'icf', codeDescription: 'Vaske sig', score: '', comment: '' },
  { code: 'FA540', codeSystem: 'icf', codeDescription: 'Af- og påklædning', score: '', comment: '' },
  { code: 'FA530', codeSystem: 'icf', codeDescription: 'Gå på toilet', score: '', comment: '' },
  { code: 'FA420', codeSystem: 'icf', codeDescription: 'Forflytte sig', score: '', comment: '' },
  { code: 'FA460', codeSystem: 'icf', codeDescription: 'Færden i forskellige omgivelser', score: '', comment: '' },
  { code: 'FA560', codeSystem: 'icf', codeDescription: 'Drikke', score: '', comment: '' },
  { code: 'FA550', codeSystem: 'icf', codeDescription: 'Spise', score: '', comment: '' },
];

function abilityCodeSystem(v: string): string {
  return (v.trim() || 'icf').toLowerCase();
}

/** Canned phrases so 4.6.1 / 4.6.4–4.6.14 stay screenshotable when xdis21-form is empty. */
export const DEFAULT_CONTAGION_PHRASES = ['Ingen kendt smitterisiko', 'MRSA', 'Anden smitterisiko — se journal'];
export const DEFAULT_NURSING_PHRASES: Record<NursingAreaCode, string[]> = {
  A: ['Bevægeapparat — se journal'],
  B: ['Ernæring — se journal'],
  C: ['Hud og slimhinder — se journal'],
  D: ['Kommunikation — se journal'],
  E: ['Psykosociale forhold — se journal'],
  F: ['Respiration og cirkulation — se journal'],
  G: ['Seksualitet — se journal'],
  H: ['Smerter og sanseindtryk — se journal'],
  I: ['Søvn og hvile — se journal'],
  J: ['Viden og udvikling — se journal'],
  K: ['Udskillelse af affaldsstoffer — se journal'],
};

export type Xdis21FormState = {
  receiver: Party;
  signedBy: SignedBy;
  admission: DateTime;
  expectedEnd: DateTime;
  expectedEndComment: string;
  agreedDischarge: DateTime;
  agreedDischargeComment: string;
  causeOfAdmission: string;
  expectedCourseOfAdmission: string;
  riskOfContagion: string;
  homeChanges: CoordinationNeed;
  scheduleAuxilaries: CoordinationNeed;
  rehabilitationNeeds: CoordinationNeed;
  homeCareNeeds: CoordinationNeed;
  medicineAdministrationHelp: CoordinationNeed;
  medicineConsumptionHelp: CoordinationNeed;
  otherMunicipalWorkNeeded: CoordinationNeed;
  psychiatryAdmissionPlanNeeded: CoordinationNeed;
  conference: {
    enabled: boolean;
    dateCode: DischargeDateCode;
    date: string;
    time: string;
    video: boolean;
    comment: string;
  };
  abilityNotRelevant: boolean;
  abilityLastModified: string;
  abilityElements: AbilityElement[];
  nursing: Record<NursingAreaCode, string>;
  reused: Record<string, unknown>;
};

export type Xdis21FormMeta = {
  psychiatryAdmissionPlanVisible: boolean;
  phrases: { riskOfContagion: string[]; nursing: Partial<Record<NursingAreaCode, string[]>> };
  abilityScores: readonly string[];
};

export type Issue = { field: string; message: string };

type Rec = Record<string, unknown>;
const rec = (v: unknown): Rec => (typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Rec) : {});
const s = (v: unknown): string => (typeof v === 'string' ? v : '');
const bool = (v: unknown): boolean => v === true || v === 'true';

const DATE = /^\d{4}-\d{2}-\d{2}$/;
const TIME = /^(([0-1][0-9])|(2[0-3])):[0-5][0-9]$/;

export const emptyNeed = (): CoordinationNeed => ({ needed: false, comment: '' });

function emptyNursing(): Record<NursingAreaCode, string> {
  return { A: '', B: '', C: '', D: '', E: '', F: '', G: '', H: '', I: '', J: '', K: '' };
}

function dtFrom(v: unknown): DateTime {
  const o = rec(v);
  return { date: s(o.date), time: s(o.time) };
}

function needFrom(v: unknown): CoordinationNeed {
  const o = rec(v);
  const comment = s(o.comment);
  const hasNeeded = Object.prototype.hasOwnProperty.call(o, 'needed') || Object.prototype.hasOwnProperty.call(o, 'value');
  if (!hasNeeded && comment) return { needed: true, comment };
  return { needed: bool(o.needed) || bool(o.value), comment };
}

function abilityFrom(v: unknown): AbilityElement[] {
  const list = Array.isArray(v) ? v : [];
  const mapped = list.map(rec).map((el) => {
    const terminology = rec(el.terminology);
    return {
      code: s(terminology.code) || s(el.code) || s(el.terminologyCode),
      codeSystem: abilityCodeSystem(s(terminology.codeSystem) || s(el.codeSystem)),
      codeDescription: s(terminology.codeDescription) || s(el.codeDescription) || s(el.description),
      score: s(el.score) === 'uden_specifikation' || s(el.score) === 'ikke_vurderet' ? '' : s(el.score),
      comment: s(el.comment),
    };
  });
  return mapped.length > 0 ? mapped : DEFAULT_ABILITY_ELEMENTS.map((el) => ({ ...el }));
}

function nursingLetter(raw: string): NursingAreaCode | null {
  const trimmed = raw.trim().toUpperCase();
  if ((NURSING_AREA_CODES as readonly string[]).includes(trimmed)) return trimmed as NursingAreaCode;
  const letter = trimmed.charAt(0);
  if ((NURSING_AREA_CODES as readonly string[]).includes(letter)) return letter as NursingAreaCode;
  return null;
}

function nursingFrom(v: unknown): Record<NursingAreaCode, string> {
  const out = emptyNursing();
  const list = Array.isArray(v) ? v : [];
  for (const item of list) {
    const o = rec(item);
    const terminology = rec(o.terminology);
    const code = nursingLetter(s(terminology.code) || s(o.code));
    if (code) out[code] = s(o.problemArea) || s(o.text) || s(o.comment);
  }
  const asObj = rec(v);
  for (const code of NURSING_AREA_CODES) {
    if (!out[code] && s(asObj[code])) out[code] = s(asObj[code]);
  }
  return out;
}

/** Psychiatry visibility as Communicate's xdis21-form / xdis21-prefill states it. */
export function psychiatryVisibleFromForm(form: Rec, speciality: string | null | undefined): boolean {
  const source = formSource(form);
  if (typeof source.psychiatryAdmissionPlanVisible === 'boolean') return source.psychiatryAdmissionPlanVisible;
  if (typeof source.showPsychiatryAdmissionPlan === 'boolean') return source.showPsychiatryAdmissionPlan;
  if (typeof source.psychiatryVisible === 'boolean') return source.psychiatryVisible;
  const fromForm = stringList(source.psychiatricDepartmentCodes);
  return isPsychiatrySpeciality(speciality, fromForm.length > 0 ? fromForm : PSYCHIATRY_SPECIALITIES);
}

function formSource(form: Rec): Rec {
  const nested = rec(form.form);
  if (Object.keys(nested).length > 0) return { ...form, ...nested };
  return form;
}

function stringList(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && x.trim().length > 0) : [];
}

export function metaFromForm(form: Rec, speciality: string | null | undefined): Xdis21FormMeta {
  const source = formSource(form);
  const phrases = rec(source.phrases);
  const nursingPhrases = rec(phrases.nursingProblemAreas ?? phrases.nursing);
  const nursing: Partial<Record<NursingAreaCode, string[]>> = {};
  for (const code of NURSING_AREA_CODES) {
    const list = stringList(nursingPhrases[code]);
    nursing[code] = list.length > 0 ? list : DEFAULT_NURSING_PHRASES[code];
  }
  const scores = stringList(source.abilityToFunctionScores ?? source.abilityScores);
  const contagion = stringList(phrases.riskOfContagion);
  return {
    psychiatryAdmissionPlanVisible: psychiatryVisibleFromForm(source, speciality),
    phrases: {
      riskOfContagion: contagion.length > 0 ? contagion : DEFAULT_CONTAGION_PHRASES,
      nursing,
    },
    abilityScores: scores.length > 0 ? scores : ABILITY_SCORES,
  };
}

/** The form state from Communicate's xdis21-prefill (no CPR in it). */
export function stateFromPrefill(prefill: Rec): Xdis21FormState {
  const receiver = rec(prefill.receiver);
  const patient = rec(prefill.patient);
  const expected = rec(prefill.expectedEndOfTreatment ?? prefill.expectedEnd);
  const agreed = rec(prefill.agreedDischarge);
  const coord = rec(prefill.furtherCoordination);
  const conference = rec(coord.dischargeConference ?? prefill.dischargeConference);
  const ability = rec(prefill.expectedAbilityToFunctionAtDischarge ?? prefill.ability);
  const relevance = s(ability.relevance) || s(ability.abilityToFunctionRelevance);
  const dateCode = s(conference.dischargeDateCode) || s(conference.dateCode);
  return {
    receiver: {
      ean: s(receiver.ean),
      identifier: s(receiver.identifier),
      identifierCode: s(receiver.identifierCode) || 'kommunenummer',
      organisationName: s(receiver.organisationName) || undefined,
      departmentName: s(receiver.departmentName) || undefined,
      unitName: s(receiver.unitName) || undefined,
      streetName: s(receiver.streetName) || undefined,
      suburbName: s(receiver.suburbName) || undefined,
      districtName: s(receiver.districtName) || undefined,
      postCodeIdentifier: s(receiver.postCodeIdentifier) || undefined,
    },
    signedBy: signedByFromPrefill(prefill),
    admission: dtFrom(prefill.admission),
    expectedEnd: dtFrom(expected),
    expectedEndComment: s(expected.comment) || s(prefill.expectedEndOfTreatmentComment),
    agreedDischarge: dtFrom(agreed),
    agreedDischargeComment: s(prefill.agreedDischargeComment) || s(agreed.comment),
    causeOfAdmission: s(prefill.causeOfAdmission),
    expectedCourseOfAdmission: s(prefill.expectedCourseOfAdmission) || s(prefill.courseOfAdmission),
    riskOfContagion: s(prefill.riskOfContagion),
    homeChanges: needFrom(coord.homeChanges),
    scheduleAuxilaries: needFrom(coord.scheduleAuxilaries),
    rehabilitationNeeds: needFrom(coord.rehabilitationNeeds),
    homeCareNeeds: needFrom(coord.homeCareNeeds),
    medicineAdministrationHelp: needFrom(coord.medicineAdministrationHelp),
    medicineConsumptionHelp: needFrom(coord.medicineConsumptionHelp),
    otherMunicipalWorkNeeded: needFrom(coord.otherMunicipalWorkNeeded),
    psychiatryAdmissionPlanNeeded: needFrom(coord.psychiatryAdmissionPlanNeeded),
    conference: {
      enabled: Boolean(s(conference.date) || s(conference.comment) || conference.videoConference === true || conference.video === true),
      dateCode: dateCode === 'aftalt' ? 'aftalt' : 'forslag',
      date: s(conference.date),
      time: s(conference.time),
      video: bool(conference.videoConference) || bool(conference.video),
      comment: s(conference.comment),
    },
    abilityNotRelevant: relevance === 'ikke_relevant' || relevance === 'ikke relevant',
    abilityLastModified: s(ability.lastModifiedDate) || todayIso(),
    abilityElements: abilityFrom(ability.elements ?? ability.abilityToFunctionElement),
    nursing: nursingFrom(prefill.nursingProblemAreas),
    reused: {
      patient,
      ...(prefill.practitioner ? { practitioner: prefill.practitioner } : {}),
      relatives: Array.isArray(prefill.relatives) ? prefill.relatives : [],
      ...(s(prefill.relativesComment) ? { relativesComment: s(prefill.relativesComment) } : {}),
    },
  };
}

export function todayIso(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Date A is strictly before date B (YYYY-MM-DD). */
export function dateBefore(a: string, b: string): boolean {
  return DATE.test(a) && DATE.test(b) && a < b;
}

export function xdis21Issues(f: Xdis21FormState, opts?: { psychiatryVisible?: boolean }): Issue[] {
  const out: Issue[] = [];
  if (!/^\d{13}$/.test(f.receiver.ean.trim())) out.push({ field: 'receiver', message: 'Modtagerens lokationsnummer skal være 13 cifre' });
  if (!f.receiver.identifier.trim()) out.push({ field: 'receiver', message: 'Modtagerens identifikator mangler' });
  if (!f.signedBy.givenName.trim() || !f.signedBy.surname.trim() || !f.signedBy.title.trim()) {
    out.push({ field: 'signedBy', message: 'Underskriver: fornavn, efternavn og titel' });
  }
  if (!DATE.test(f.admission.date)) out.push({ field: 'admission', message: 'Indlæggelse: vælg en dato' });
  if (!TIME.test(f.admission.time)) out.push({ field: 'admission', message: 'Indlagt: angiv klokkeslæt (TT:MM)' });
  if (!f.causeOfAdmission.trim()) out.push({ field: 'causeOfAdmission', message: 'Indlæggelsesårsag er påkrævet' });

  const hasExpectedDate = DATE.test(f.expectedEnd.date);
  if (hasExpectedDate && dateBefore(f.expectedEnd.date, f.admission.date)) {
    out.push({ field: 'expectedEnd', message: 'Forventet udskrivningsdato kan ikke være tidligere end indlæggelsesdatoen' });
  }
  if (hasExpectedDate && !TIME.test(f.expectedEnd.time)) {
    out.push({ field: 'expectedEnd', message: 'Forventet udskrivning: angiv klokkeslæt (TT:MM), når datoen er udfyldt' });
  }
  if (!hasExpectedDate && !f.expectedEndComment.trim()) {
    out.push({ field: 'expectedEndComment', message: 'Kommentar skal udfyldes, når forventet dato mangler' });
  }

  const hasAgreedDate = DATE.test(f.agreedDischarge.date);
  const hasAgreedTime = TIME.test(f.agreedDischarge.time);
  if (hasAgreedDate && !hasAgreedTime) {
    out.push({ field: 'agreedDischarge', message: 'Aftalt udskrivningsdato: angiv klokkeslæt (TT:MM), når datoen er udfyldt' });
  } else if (f.agreedDischarge.time && !hasAgreedDate) {
    out.push({ field: 'agreedDischarge', message: 'Aftalt udskrivningsdato: vælg en dato, når klokkeslættet er udfyldt' });
  } else if (hasAgreedDate && hasAgreedTime && dateBefore(f.agreedDischarge.date, f.admission.date)) {
    out.push({ field: 'agreedDischarge', message: 'Aftalt udskrivningsdato kan ikke være tidligere end indlæggelsesdatoen' });
  }

  if (f.conference.enabled) {
    if (f.conference.dateCode !== 'forslag' && f.conference.dateCode !== 'aftalt') {
      out.push({ field: 'conference', message: 'Dato for udskrivningskonference skal markeres som forslag eller aftalt' });
    }
    if (!DATE.test(f.conference.date)) out.push({ field: 'conference', message: 'Udskrivningskonference: vælg en dato' });
    if (f.conference.time && !TIME.test(f.conference.time)) {
      out.push({ field: 'conference', message: 'Udskrivningskonference: angiv klokkeslæt (TT:MM)' });
    }
    if (!f.conference.comment.trim()) {
      out.push({ field: 'conference', message: 'Skriv en kommentar til udskrivningskonferencen' });
    }
  }

  const coord: Array<[CoordinationNeed, string]> = [
    [f.homeChanges, 'homeChanges'],
    [f.scheduleAuxilaries, 'scheduleAuxilaries'],
    [f.homeCareNeeds, 'homeCareNeeds'],
    [f.medicineAdministrationHelp, 'medicineAdministrationHelp'],
    [f.medicineConsumptionHelp, 'medicineConsumptionHelp'],
    [f.otherMunicipalWorkNeeded, 'otherMunicipalWorkNeeded'],
  ];
  for (const [need, field] of coord) {
    if (need.needed && !need.comment.trim()) out.push({ field, message: 'Skriv en kommentar, når der er behov' });
  }
  if (opts?.psychiatryVisible && f.psychiatryAdmissionPlanNeeded.needed && !f.psychiatryAdmissionPlanNeeded.comment.trim()) {
    out.push({ field: 'psychiatryAdmissionPlanNeeded', message: 'Skriv en kommentar, når der er behov' });
  }

  if (!DATE.test(f.abilityLastModified)) {
    out.push({ field: 'ability', message: 'Funktionsevne: sidst ændret-dato skal udfyldes' });
  }
  if (!f.abilityNotRelevant) {
    const coded = f.abilityElements.filter((el) => el.code.trim());
    if (coded.length === 0) {
      out.push({ field: 'ability', message: 'Funktionsevne: angiv mindst én funktion med en kode' });
    }
    const missingScore = coded.some((el) => !selectableAbilityScores([el.score]).length);
    if (missingScore) out.push({ field: 'ability', message: 'Funktionsevne: vælg en score for hver funktion' });
  }

  return out;
}

const opt = (v: string) => (v.trim() ? v.trim() : undefined);

function needOut(n: CoordinationNeed) {
  return { needed: n.needed, ...(opt(n.comment) ? { comment: opt(n.comment) } : {}) };
}

/** The plan body for communicate-xdis build21 (Communicate ref-preview "xdis21"). */
export function xdis21Payload(f: Xdis21FormState, sender: Rec, opts?: { psychiatryVisible?: boolean }): Rec {
  const further: Rec = {};
  for (const key of COORDINATION_SUBJECTS) {
    further[key] =
      key === 'psychiatryAdmissionPlanNeeded' && !opts?.psychiatryVisible
        ? { needed: false }
        : needOut(f[key]);
  }
  if (f.conference.enabled) {
    further.dischargeConference = {
      dischargeDateCode: f.conference.dateCode,
      date: f.conference.date,
      ...(TIME.test(f.conference.time) ? { time: f.conference.time } : {}),
      videoConference: f.conference.video,
      ...(opt(f.conference.comment) ? { comment: opt(f.conference.comment) } : {}),
    };
  }

  const ability: Rec = {
    lastModifiedDate: DATE.test(f.abilityLastModified) ? f.abilityLastModified : todayIso(),
  };
  if (f.abilityNotRelevant) {
    ability.relevance = 'ikke_relevant';
  } else {
    const elements = f.abilityElements
      .filter((el) => el.code.trim())
      .map((el) => ({
        code: el.code.trim(),
        codeSystem: abilityCodeSystem(el.codeSystem),
        codeDescription: el.codeDescription.trim(),
        score: el.score,
        ...(opt(el.comment) ? { comment: opt(el.comment) } : {}),
      }));
    if (elements.length > 0) ability.elements = elements;
  }

  const nursing = NURSING_AREA_CODES.filter((code) => f.nursing[code].trim()).map((code) => ({
    code,
    text: f.nursing[code].trim(),
  }));

  const expectedEnd: Rec = {};
  if (DATE.test(f.expectedEnd.date)) {
    expectedEnd.date = f.expectedEnd.date;
    if (TIME.test(f.expectedEnd.time)) expectedEnd.time = f.expectedEnd.time;
  }
  if (opt(f.expectedEndComment)) expectedEnd.comment = opt(f.expectedEndComment);

  return {
    ...f.reused,
    sender: { ...sender, signedBy: f.signedBy },
    receiver: f.receiver,
    admission: {
      date: f.admission.date,
      time: f.admission.time,
    },
    expectedEndOfTreatment: expectedEnd,
    ...(DATE.test(f.agreedDischarge.date) && TIME.test(f.agreedDischarge.time)
      ? {
          agreedDischarge: { date: f.agreedDischarge.date, time: f.agreedDischarge.time },
          ...(opt(f.agreedDischargeComment) ? { agreedDischargeComment: opt(f.agreedDischargeComment) } : {}),
        }
      : opt(f.agreedDischargeComment)
        ? { agreedDischargeComment: opt(f.agreedDischargeComment) }
        : {}),
    causeOfAdmission: f.causeOfAdmission,
    expectedCourseOfAdmission: opt(f.expectedCourseOfAdmission),
    riskOfContagion: opt(f.riskOfContagion),
    ...(Object.keys(further).length > 0 ? { furtherCoordination: further } : {}),
    expectedAbilityToFunctionAtDischarge: ability,
    ...(nursing.length > 0 ? { nursingProblemAreas: nursing } : {}),
  };
}

export function reuseSourceOf(prefill: Rec): 'xdis21' | 'xdis16' {
  const raw = s(prefill.reuseSource) || s(prefill.reusedFrom) || s(prefill.source);
  if (/xdis21|carePlan|pleje/i.test(raw)) return 'xdis21';
  return 'xdis16';
}
